import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as THREE from "three";

/**
 * Interactive Three.js icosahedron with HTML nodes attached to fixed vertex anchors.
 * - Auto-rotates continuously
 * - User can drag to rotate
 * - Pointer magnet: nearest node within range is pulled toward the pointer (DOM offset, no 3D shake)
 * - Click → fires onNodeClick(key) (parent opens modal). No page navigation.
 */
export default function ThreeHero({ className = "", nodes = [], onNodeClick }) {
  const mountRef = useRef(null);
  const buttonsRef = useRef([]);
  const linesRef = useRef([]);
  const tipsRef = useRef([]);
  const stateRef = useRef({
    autoY: 0,
    autoX: 0,
    dragging: false,
    lastX: 0,
    lastY: 0,
    extraY: 0,
    extraX: 0,
    pointerX: -9999,
    pointerY: -9999,
    pointerActive: false,
  });
  const [hoverIdx, setHoverIdx] = useState(-1);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const width = mount.clientWidth;
    const height = mount.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
    camera.position.z = 6;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);
    renderer.domElement.style.touchAction = "none";
    renderer.domElement.style.cursor = "grab";

    const geom = new THREE.IcosahedronGeometry(1.6, 1);
    const mat = new THREE.MeshBasicMaterial({ color: 0x1e3a5f, wireframe: true, transparent: true, opacity: 0.85 });
    const mesh = new THREE.Mesh(geom, mat);
    scene.add(mesh);

    const coreGeom = new THREE.IcosahedronGeometry(0.55, 0);
    const coreMat = new THREE.MeshBasicMaterial({ color: 0x0a0a0a });
    const core = new THREE.Mesh(coreGeom, coreMat);
    scene.add(core);

    const directions = [
      new THREE.Vector3( 0.95,  0.35,  0.30),
      new THREE.Vector3(-0.85,  0.55,  0.20),
      new THREE.Vector3(-0.40, -0.85,  0.40),
      new THREE.Vector3( 0.55, -0.70, -0.45),
      new THREE.Vector3( 0.10,  0.95, -0.30),
    ].map((v) => v.normalize());

    // Tip = on sphere surface, Label = outside sphere
    const TIP_RADIUS = 1.6;     // matches icosahedron radius
    const LABEL_RADIUS = 2.0;   // closer to sphere — half the previous offset

    const markerGeom = new THREE.SphereGeometry(0.06, 14, 14);
    const markers = directions.map(() => {
      const m = new THREE.Mesh(markerGeom, new THREE.MeshBasicMaterial({ color: 0x1e3a5f }));
      scene.add(m);
      return m;
    });

    const pCount = 200;
    const positionsArr = new Float32Array(pCount * 3);
    for (let i = 0; i < pCount; i++) {
      positionsArr[i * 3] = (Math.random() - 0.5) * 8;
      positionsArr[i * 3 + 1] = (Math.random() - 0.5) * 6;
      positionsArr[i * 3 + 2] = (Math.random() - 0.5) * 4;
    }
    const pGeom = new THREE.BufferGeometry();
    pGeom.setAttribute("position", new THREE.BufferAttribute(positionsArr, 3));
    const pMat = new THREE.PointsMaterial({ color: 0x0a0a0a, size: 0.025, transparent: true, opacity: 0.5 });
    const points = new THREE.Points(pGeom, pMat);
    scene.add(points);

    // ---- Drag + pointer tracking ----
    const canvas = renderer.domElement;
    const onPointerDown = (e) => {
      stateRef.current.dragging = true;
      stateRef.current.lastX = e.clientX;
      stateRef.current.lastY = e.clientY;
      canvas.style.cursor = "grabbing";
      canvas.setPointerCapture?.(e.pointerId);
    };
    const onPointerMove = (e) => {
      const rect = mount.getBoundingClientRect();
      stateRef.current.pointerX = e.clientX - rect.left;
      stateRef.current.pointerY = e.clientY - rect.top;
      stateRef.current.pointerActive =
        stateRef.current.pointerX >= 0 &&
        stateRef.current.pointerY >= 0 &&
        stateRef.current.pointerX <= rect.width &&
        stateRef.current.pointerY <= rect.height;

      if (stateRef.current.dragging) {
        const dx = e.clientX - stateRef.current.lastX;
        const dy = e.clientY - stateRef.current.lastY;
        stateRef.current.extraY += dx * 0.005;
        stateRef.current.extraX += dy * 0.005;
        stateRef.current.lastX = e.clientX;
        stateRef.current.lastY = e.clientY;
      }
    };
    const onPointerUp = (e) => {
      stateRef.current.dragging = false;
      canvas.style.cursor = "grab";
      canvas.releasePointerCapture?.(e.pointerId);
    };
    const onPointerLeave = () => {
      stateRef.current.pointerActive = false;
      stateRef.current.pointerX = -9999;
      stateRef.current.pointerY = -9999;
    };
    canvas.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointerleave", onPointerLeave);

    let raf;
    const clock = new THREE.Clock();
    const tmp = new THREE.Vector3();

    const animate = () => {
      const dt = clock.getDelta();
      // Constant auto-rotation
      stateRef.current.autoY += dt * 0.15;
      stateRef.current.autoX += dt * 0.05;

      mesh.rotation.y = stateRef.current.autoY + stateRef.current.extraY;
      mesh.rotation.x = stateRef.current.autoX + stateRef.current.extraX;
      core.rotation.x = -stateRef.current.autoY * 1.4;
      core.rotation.y = -stateRef.current.autoY * 1.1;
      points.rotation.y = stateRef.current.autoY * 0.3;

      const px = stateRef.current.pointerX;
      const py = stateRef.current.pointerY;
      const pointerActive = stateRef.current.pointerActive;

      // Project both tip (on sphere) and label (offset) for each direction
      const projections = directions.map((dir, i) => {
        const tipWorld = tmp.copy(dir).multiplyScalar(TIP_RADIUS).applyEuler(mesh.rotation);
        markers[i].position.copy(tipWorld);
        const tipProj = tipWorld.clone().project(camera);
        const tipX = (tipProj.x * 0.5 + 0.5) * width;
        const tipY = (-tipProj.y * 0.5 + 0.5) * height;

        const labelWorld = new THREE.Vector3().copy(dir).multiplyScalar(LABEL_RADIUS).applyEuler(mesh.rotation);
        const labelProj = labelWorld.clone().project(camera);
        const labelX = (labelProj.x * 0.5 + 0.5) * width;
        const labelY = (-labelProj.y * 0.5 + 0.5) * height;

        const visible = labelProj.z < 1 && labelWorld.z > -0.4;
        return { tipX, tipY, labelX, labelY, visible };
      });

      // Find nearest visible node to pointer
      let nearest = -1;
      let nearestDist = Infinity;
      if (pointerActive) {
        projections.forEach((p, i) => {
          if (!p.visible) return;
          const d = Math.hypot(p.labelX - px, p.labelY - py);
          if (d < nearestDist) { nearestDist = d; nearest = i; }
        });
      }
      const MAGNET_RADIUS = 140;
      const magnetIdx = nearestDist < MAGNET_RADIUS ? nearest : -1;

      projections.forEach((p, i) => {
        const btn = buttonsRef.current[i];
        const line = linesRef.current[i];
        const tip = tipsRef.current[i];
        if (!btn) return;
        let ox = p.labelX;
        let oy = p.labelY;
        let scale = 1;
        if (i === magnetIdx) {
          const t = 1 - nearestDist / MAGNET_RADIUS;
          const pull = Math.min(0.55, t * 0.65);
          ox = p.labelX + (px - p.labelX) * pull;
          oy = p.labelY + (py - p.labelY) * pull;
          scale = 1 + t * 0.18;
          markers[i].scale.setScalar(1 + t * 0.7);
          markers[i].material.color.setHex(0x1e3a5f);
        } else {
          markers[i].scale.setScalar(1);
          markers[i].material.color.setHex(0x1e3a5f);
        }
        btn.style.left = `${ox}px`;
        btn.style.top = `${oy}px`;
        btn.style.transform = `translate(-50%, -50%) scale(${scale})`;
        btn.style.opacity = p.visible ? "1" : "0";
        btn.style.pointerEvents = p.visible ? "auto" : "none";
        btn.dataset.magnet = i === magnetIdx ? "1" : "0";

        // SVG line from sphere tip → halfway toward label (half-length, attached to sphere)
        if (line) {
          const midX = p.tipX + (ox - p.tipX) * 0.5;
          const midY = p.tipY + (oy - p.tipY) * 0.5;
          line.setAttribute("x1", p.tipX);
          line.setAttribute("y1", p.tipY);
          line.setAttribute("x2", midX);
          line.setAttribute("y2", midY);
          line.setAttribute("opacity", p.visible ? (i === magnetIdx ? "0.95" : "0.6") : "0");
          line.setAttribute("stroke", i === magnetIdx ? "#1E3A5F" : "#0A0A0A");
        }
        if (tip) {
          tip.setAttribute("cx", p.tipX);
          tip.setAttribute("cy", p.tipY);
          tip.setAttribute("opacity", p.visible ? "1" : "0");
          tip.setAttribute("r", i === magnetIdx ? "5" : "3.5");
        }
      });

      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    const onResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      canvas.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointerleave", onPointerLeave);
      window.removeEventListener("resize", onResize);
      mount.removeChild(canvas);
      [geom, mat, coreGeom, coreMat, markerGeom, pGeom, pMat].forEach((o) => o.dispose && o.dispose());
      markers.forEach((m) => m.material.dispose());
      renderer.dispose();
    };
  }, []);

  return (
    <div className={className}>
      <div ref={mountRef} className="absolute inset-0" aria-hidden="true" />
      <svg className="absolute inset-0 pointer-events-none" width="100%" height="100%" aria-hidden="true">
        {nodes.map((node, i) => (
          <g key={`g-${node.label}`}>
            <line
              ref={(el) => (linesRef.current[i] = el)}
              x1="0" y1="0" x2="0" y2="0"
              stroke="#0A0A0A"
              strokeWidth="1"
              strokeDasharray="2 4"
              opacity="0"
              style={{ transition: "opacity 200ms ease, stroke 200ms ease" }}
            />
            <circle
              ref={(el) => (tipsRef.current[i] = el)}
              cx="0" cy="0" r="3.5"
              fill="#1E3A5F"
              opacity="0"
              style={{ transition: "opacity 200ms ease, r 200ms ease" }}
            />
          </g>
        ))}
      </svg>
      <div className="absolute inset-0 pointer-events-none">
        {nodes.map((node, i) => {
          const isHover = hoverIdx === i;
          return (
            <button
              key={node.label}
              ref={(el) => (buttonsRef.current[i] = el)}
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(-1)}
              onClick={(e) => {
                e.preventDefault();
                onNodeClick?.(node.key, node);
              }}
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                opacity: 0,
                transition: "background-color 240ms, color 240ms, border-color 240ms, box-shadow 240ms",
                willChange: "left, top, transform, opacity",
              }}
              className={`pointer-events-auto group flex items-center gap-2 px-3 py-1.5 rounded-full font-mono-aq text-[0.62rem] uppercase tracking-[0.22em] whitespace-nowrap ${
                isHover
                  ? "bg-[#0A0A0A] text-[#EAE4D5] border border-[#1E3A5F] shadow-[0_8px_24px_rgba(30,58,95,0.35)]"
                  : "bg-white backdrop-blur-md text-[#0A0A0A] border border-[#B6B09F]/60"
              }`}
              data-testid={`hero-node-${node.key}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${isHover ? "bg-[#EAE4D5]" : "bg-[#1E3A5F]"}`} />
              {node.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Modal shell used by the parent to render rich content for each node.
 */
export function NodeModal({ open, onClose, title, eyebrow, children }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 md:p-8"
          data-testid="node-modal"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <motion.div
            initial={{ scale: 0.85, opacity: 0, y: 30 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl aq-glass rounded-3xl overflow-hidden max-h-[85vh] flex flex-col"
          >
            <div className="flex items-start justify-between px-8 md:px-12 pt-10 pb-6">
              <div>
                <div className="label-eyebrow mb-3">— {eyebrow}</div>
                <h2 className="font-serif-display text-4xl md:text-5xl tracking-tighter">{title}</h2>
              </div>
              <button
                onClick={onClose}
                className="w-10 h-10 rounded-full border border-[#B6B09F]/60 flex items-center justify-center hover:bg-[#0A0A0A] hover:text-[#EAE4D5] transition-colors"
                aria-label="Close"
                data-testid="modal-close"
              >
                ✕
              </button>
            </div>
            <div className="px-8 md:px-12 pb-10 overflow-y-auto">{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
